import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import type { ReactNode } from "react";

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  image: string;
  imageAlt: string;
  breadcrumbLabel: string;
  children: ReactNode;
}

export default function ArticleLayout({
  title,
  subtitle,
  image,
  imageAlt,
  breadcrumbLabel,
  children,
}: ArticleLayoutProps) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || "https://sugargoospread.pics";
  const canonicalUrl = `${siteUrl}${location.pathname}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: subtitle,
    image,
    url: canonicalUrl,
    datePublished: "2026-08-04",
    dateModified: "2026-08-04",
    author: {
      "@type": "Organization",
      name: "Sugargoo Spreadsheet",
    },
    publisher: {
      "@type": "Organization",
      name: "Sugargoo Spreadsheet",
      logo: {
        "@type": "ImageObject",
        url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />
          {/* Breadcrumb */}
          <nav className="text-xs text-foreground-500 mb-6 flex items-center gap-2 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-700">{breadcrumbLabel}</span>
          </nav>

          <h1 className="text-2xl md:text-4xl text-foreground-900 leading-tight">
            {title}
          </h1>

          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
            {subtitle}
          </p>

          <div className="mt-8 rounded-2xl overflow-hidden">
            <img
              src={image}
              alt={imageAlt}
              title={`${breadcrumbLabel} - Sugargoo Spreadsheet`}
              className="w-full"
            />
          </div>

          <div className="mt-10 text-foreground-700 leading-relaxed space-y-6">
            {children}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}